"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useAction } from "convex/react";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { api } from "../../../../convex/_generated/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckIcon } from "@/components/icons";
import { usePageTitle } from "@/lib/usePageTitle";
import { useEntitlementVerify } from "@/hooks/useEntitlementVerify";
import { useSubscription } from "@/hooks/useSubscription";
import {
  PRICING_PLANS,
  SERVER_LIMITS_BY_TIER,
  type SubscriptionTier,
} from "@/lib/billing/plans";

function planDisplayName(plan: string | null): string | null {
  if (!plan) return null;
  const match = PRICING_PLANS.find((p) => p.id === plan);
  return match?.name ?? null;
}

export default function PaymentSuccessPage() {
  usePageTitle("Payment Successful");

  // Fire a fresh Polar.sh entitlement check on mount to warm the
  // polarEntitlementCache after checkout, giving any subsequent premium-route
  // SubscriptionGate an authoritative Polar API result.
  useEntitlementVerify();

  const searchParams = useSearchParams();
  const router = useRouter();
  const sub = useSubscription();
  const getStripeSessionStatus = useAction(api.actions.getStripeSessionStatus);

  const sessionId = searchParams.get("session_id");
  const planParam = searchParams.get("plan");
  const requestedPlan = planDisplayName(planParam);

  // Stripe session validation
  const [stripeStatus, setStripeStatus] = useState<{
    status: string;
    paymentStatus: string;
    customerEmail: string | null;
    plan: string | null;
  } | null>(null);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [stripeLoading, setStripeLoading] = useState(false);

  // Auto-redirect countdown
  const [countdown, setCountdown] = useState<number | null>(null);

  // Validate Stripe session on mount
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    setStripeLoading(true);
    getStripeSessionStatus({ sessionId })
      .then((result) => {
        if (cancelled) return;
        if (result.ok && result.data) {
          setStripeStatus(result.data);
        } else {
          setStripeError(
            result.ok ? "Unknown error" : result.error.message
          );
        }
      })
      .catch((err) => {
        if (!cancelled)
          setStripeError(
            err instanceof Error ? err.message : "Failed to validate session"
          );
      })
      .finally(() => {
        if (!cancelled) setStripeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, getStripeSessionStatus]);

  const isActivated =
    !sub.isLoading &&
    sub.tier !== "free" &&
    (sub.status === "active" || sub.status === "trialing");

  // Auto-redirect to dashboard once subscription is confirmed
  useEffect(() => {
    if (!isActivated) return;
    setCountdown(5);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(interval);
          router.replace("/dashboard?checkout=success");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isActivated, router]);

  const serverLimit = isActivated
    ? SERVER_LIMITS_BY_TIER[sub.tier] ?? 0
    : 0;

  const renewalDate = sub.currentPeriodEnd
    ? new Date(sub.currentPeriodEnd).toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;

  const trialEndDate =
    sub.isTrialing && sub.trialEndsAt
      ? new Date(sub.trialEndsAt).toLocaleDateString(undefined, {
          month: "long",
          day: "numeric",
          year: "numeric",
        })
      : null;

  return (
    <div className="mx-auto max-w-3xl space-y-6 px-4 py-8">
      {/* Checkout confirmation banner */}
      <Card className="border-green-300 bg-green-50/60 dark:border-green-700 dark:bg-green-950/20">
        <CardHeader>
          <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-green-600/15 text-green-700 dark:text-green-300">
            <CheckIcon className="h-5 w-5" />
          </div>
          <CardTitle>Payment Successful</CardTitle>
          <CardDescription>
            Stripe accepted your payment. We're syncing your subscription now.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted">
          {requestedPlan && (
            <p>
              Selected plan:{" "}
              <span className="font-medium text-foreground">
                {requestedPlan}
              </span>
            </p>
          )}
          {sessionId && (
            <p>
              Session reference:{" "}
              <span className="font-mono text-xs text-foreground">
                {sessionId}
              </span>
            </p>
          )}
          {stripeLoading && (
            <p className="text-muted">Validating payment session...</p>
          )}
          {stripeError && (
            <p className="text-red-600">
              Session validation failed: {stripeError}
            </p>
          )}
          {stripeStatus && (
            <p>
              Payment status:{" "}
              <span className="font-medium capitalize text-foreground">
                {stripeStatus.paymentStatus}
              </span>
            </p>
          )}
        </CardContent>
      </Card>

      {/* Subscription status card */}
      {sub.isLoading ? (
        <Card>
          <CardHeader>
            <CardTitle>Confirming Subscription</CardTitle>
            <CardDescription>
              Waiting for your subscription status to sync from Stripe.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Skeleton className="h-4 w-44" />
            <Skeleton className="h-4 w-64" />
            <Skeleton className="h-4 w-52" />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="space-y-2">
            <div className="flex items-center gap-2">
              <CardTitle>Subscription Status</CardTitle>
              <Badge variant={isActivated ? "success" : "secondary"}>
                {isActivated ? "Active" : "Pending"}
              </Badge>
            </div>
            <CardDescription>
              {isActivated
                ? "Your subscription is confirmed and active."
                : "Your payment was received. Waiting for the subscription to activate."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted">
            <p>
              Plan:{" "}
              <span className="font-medium text-foreground">
                {sub.planName ?? "Free"}
              </span>
            </p>
            <p>
              Status:{" "}
              <span className="font-medium capitalize text-foreground">
                {sub.status === "none"
                  ? "No active subscription"
                  : sub.status.replace(/_/g, " ")}
              </span>
            </p>
            {serverLimit > 0 && (
              <p>
                Organization limit:{" "}
                <span className="font-medium text-foreground">
                  {serverLimit}
                </span>
              </p>
            )}
            {trialEndDate && (
              <p>
                Trial ends:{" "}
                <span className="font-medium text-foreground">
                  {trialEndDate}
                </span>
              </p>
            )}
            {!sub.isTrialing && renewalDate && (
              <p>
                Next billing date:{" "}
                <span className="font-medium text-foreground">
                  {renewalDate}
                </span>
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Server access instructions - shown once subscription is active */}
      {isActivated && (
        <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/20">
          <CardHeader>
            <CardTitle className="text-base">
              Getting Started with Your Server
            </CardTitle>
            <CardDescription>
              Your {sub.planName} plan is live. Here's how to set up your game
              server.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <ol className="list-inside list-decimal space-y-2 text-muted">
              <li>
                <span className="text-foreground font-medium">
                  Create an organization
                </span>{" "}
                — head to your dashboard and click "Create Organization" to set
                up your server community.
              </li>
              <li>
                <span className="text-foreground font-medium">
                  Install the BlockyNetwork plugin
                </span>{" "}
                — download the plugin JAR from your organization settings and
                add it to your Hytale server's{" "}
                <code className="rounded bg-muted/50 px-1 py-0.5 text-xs">
                  plugins/
                </code>{" "}
                folder.
              </li>
              <li>
                <span className="text-foreground font-medium">
                  Link your server
                </span>{" "}
                — use the API key from your organization settings to connect
                your Hytale server. The plugin will begin sending heartbeats
                automatically.
              </li>
              <li>
                <span className="text-foreground font-medium">
                  Go live in Server Finder
                </span>{" "}
                — once linked, your server appears in the BlockyNetworks Server
                Finder for players to discover.
              </li>
            </ol>
            {serverLimit > 0 && (
              <p className="text-xs text-muted">
                Your plan supports up to{" "}
                <span className="font-medium text-foreground">
                  {serverLimit}
                </span>{" "}
                organization{serverLimit !== 1 ? "s" : ""}.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Auto-redirect notice */}
      {isActivated && countdown !== null && countdown > 0 && (
        <p className="text-sm text-muted">
          Redirecting to dashboard in {countdown}s&hellip;{" "}
          <button
            onClick={() => {
              setCountdown(null);
              router.replace("/dashboard?checkout=success");
            }}
            className="text-primary hover:underline"
          >
            Go now
          </button>
        </p>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-3">
        <Link href="/dashboard">
          <Button>Go to Dashboard</Button>
        </Link>
        <Link href="/dashboard/settings/billing">
          <Button variant="outline">Manage Billing</Button>
        </Link>
      </div>
    </div>
  );
}
